import type { CellContext } from '@tanstack/react-table';
import { useUpdateFeatureFlagsMutation } from 'gcs-common/clients/api/entities/feature-flags/feature-flags-api';
import type { FetchFeatureFlagsResponseData } from 'gcs-common/clients/api/entities/feature-flags/feature-flags-api-helpers';
import { fetchUserSelectOptions } from 'gcs-common/slices/users/usersThunks';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { GroupBase } from 'react-select';
import AsyncSelect, { type AsyncProps } from 'react-select/async';
import { useDebouncedCallback } from 'use-debounce';
import EditingCell from '../EditingCell/EditingCell';

interface OptionType {
  value: string;
  label: string;
}

type LoadOptionsParameters = Parameters<
  NonNullable<AsyncProps<OptionType, true, GroupBase<OptionType>>['loadOptions']>
>;

const calculateUserName = (user: FetchFeatureFlagsResponseData['data'][number]['users'][number]) => {
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  return user.name || user.userName;
};

const calculateUserLabel = (user: FetchFeatureFlagsResponseData['data'][number]['users'][number]) => {
  return `${calculateUserName(user)} (${user.email || user.phoneNumber}) (${user.businessUnitOrTenantName ?? '-'})`;
};

interface FeatureFlagUsersCellProps {
  cell: CellContext<FetchFeatureFlagsResponseData['data'][number], unknown>['cell'];
  value: FetchFeatureFlagsResponseData['data'][number]['users'];
}
const FeatureFlagUsersCell = (props: FeatureFlagUsersCellProps) => {
  const { cell, value: users } = props;

  const { mutateAsync, isPending } = useUpdateFeatureFlagsMutation();

  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [options, setOptions] = useState<OptionType[]>([]);

  const featureFlagId = cell.row.original.id;

  const fetchOptions = async (...args: LoadOptionsParameters) => {
    const [search, callbackFn] = args;
    const userSelectOptions = await dispatch(fetchUserSelectOptions(search));

    callbackFn(
      // @ts-expect-error // TODO refactor soon
      userSelectOptions.map((user) => ({
        label: calculateUserLabel(user),
        value: user.id,
      })),
    );
  };

  const [fetchOptionsDebounced] = useDebouncedCallback<LoadOptionsParameters>((...args) => {
    return fetchOptions(...args);
  }, 500);

  const setInitialOptions = () => {
    const initialOptions = users.map((user) => ({ label: calculateUserLabel(user), value: user.id }));
    setOptions(initialOptions);
  };

  useEffect(() => {
    setInitialOptions();
  }, []);

  const save = async () => {
    const userIds = options?.map((option) => option.value) || [];

    await mutateAsync({
      path: {
        feature_flag_id: featureFlagId,
      },
      body: {
        userIds,
      },
    });
  };

  const cancel = () => {
    setInitialOptions();
  };

  return (
    <EditingCell onSave={save} isEditing={isEditing} setIsEditing={setIsEditing} onCancel={cancel} disabled={isPending}>
      <AsyncSelect<{ value: string; label: string }, true>
        isMulti
        loadOptions={fetchOptionsDebounced}
        isDisabled={!isEditing || isPending}
        isLoading={isPending}
        value={options}
        onChange={(selected) => {
          setOptions([...selected]);
        }}
        styles={{ container: (base) => ({ ...base, minWidth: '300px' }) }}
      />
    </EditingCell>
  );
};

export default FeatureFlagUsersCell;
